import { useMemo } from 'react';
import { C } from '../labs/wsShared';
import ReportComposer from '../labs/ReportComposer';
import { MLReportTab } from '../labs/MLLab';

const MONO = "'JetBrains Mono',monospace";
const SYNE = "'Syne',sans-serif";

const TYPE_META = {
  auto_eda:       { label: 'AUTO EDA',      icon: '◉', color: C.cyan   },
  ml_experiments: { label: 'ML EXPERIMENT', icon: '⚡', color: C.orange },
  dl_experiments: { label: 'DL EXPERIMENT', icon: '🧠', color: C.green  },
};

function DLPublicReport({ exp, snap, ds }) {
  const prepared = snap.prepared || {};
  const mlExperiment = useMemo(() => ({
    name: exp.title || 'DL Experiment',
    readOnly: true,
    reportState: snap.report_state || null,
    ds,
    mlConfig: {
      targetCol: snap.ml_config?.targetCol || prepared.target || '',
      featureCols: snap.ml_config?.featureCols || prepared.features || [],
      encodings: snap.ml_config?.encodings || {},
      scalingMethod: snap.ml_config?.scalingMethod || {},
      pcaResult: snap.ml_config?.pcaResult || null,
    },
    mlResults: {
      ann_model: {
        algoId: 'ann_model',
        algoName: 'Neural Network (ANN)',
        color: C.green,
        taskType: prepared.task || 'classification',
        metrics: prepared.task === 'regression'
          ? {
              mae: snap.eval_result?.mae ?? 0,
              mse: snap.eval_result?.mse ?? 0,
              rmse: snap.eval_result?.rmse ?? 0,
              r2: snap.eval_result?.r2 ?? 0,
            }
          : {
              accuracy: snap.eval_result?.testAcc ?? snap.train_state?.valAcc ?? 0,
              precision: snap.val_metrics?.precision ?? 0,
              recall: snap.val_metrics?.recall ?? 0,
              f1: snap.val_metrics?.f1 ?? 0,
            },
        nTrain: Math.max((prepared.rows_used || 0) - Math.round((prepared.rows_used || 0) * (prepared.split || 0.2)), 0),
        nTest: Math.round((prepared.rows_used || 0) * (prepared.split || 0.2)),
      },
    },
  }), [exp.title, snap, ds, prepared]);

  const history = snap.training_history || [];
  const classLabels = prepared.class_list || [];
  const valReport = snap.val_metrics?.report || [];
  const confusion = snap.val_metrics?.confusion || [];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <MLReportTab
        experiment={mlExperiment}
        dashboardCards={snap.dashboard_cards || []}
        reportState={snap.report_state || null}
        readOnly
      />

      <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '18px 20px' }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.green, letterSpacing: '.12em', fontWeight: 700, marginBottom: 12 }}>
          DL EXPERIMENT DETAILS
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8, marginBottom: 14 }}>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>TASK</div><div style={{ color: C.t1, fontWeight: 700 }}>{String(prepared.task || 'classification').toUpperCase()}</div></div>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>TARGET</div><div style={{ color: C.t1, fontWeight: 700 }}>{prepared.target || '—'}</div></div>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>ARCHITECTURE</div><div style={{ color: C.t1, fontWeight: 700 }}>{snap.architecture?.hidden_layers || '—'}</div></div>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>OPTIMIZER</div><div style={{ color: C.t1, fontWeight: 700 }}>{String(snap.architecture?.optimizer || '—').toUpperCase()}</div></div>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>EPOCHS</div><div style={{ color: C.t1, fontWeight: 700 }}>{snap.architecture?.epochs ?? 0}</div></div>
          <div style={{ padding: '8px 10px', border: `1px solid ${C.b1}`, borderRadius: 4, background: C.deep }}><div style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>LEARNING RATE</div><div style={{ color: C.t1, fontWeight: 700 }}>{snap.architecture?.learning_rate ?? 0}</div></div>
        </div>

        {history.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.t4, letterSpacing: '.12em', marginBottom: 6 }}>TRAINING HISTORY</div>
            <div style={{ maxHeight: 220, overflow: 'auto', border: `1px solid ${C.b0}`, borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: C.deep }}>
                    {['EPOCH', 'TRAIN LOSS', 'VAL LOSS', 'TRAIN ACC', 'VAL ACC'].map((label) => (
                      <th key={label} style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => (
                    <tr key={row.epoch}>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.epoch}</td>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.trainLoss}</td>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.valLoss}</td>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.trainAcc}</td>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.valAcc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {valReport.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.t4, letterSpacing: '.12em', marginBottom: 6 }}>CLASSIFICATION REPORT</div>
            <div style={{ overflow: 'auto', border: `1px solid ${C.b0}`, borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: C.deep }}>
                    {['CLASS', 'PRECISION', 'RECALL', 'F1', 'SUPPORT'].map((label) => (
                      <th key={label} style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {valReport.map((row) => (
                    <tr key={row.classIndex}>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{classLabels[row.classIndex] || `Class ${row.classIndex}`}</td>
                      <td style={{ padding: '6px 8px', color: C.green, borderBottom: `1px solid ${C.b0}` }}>{row.precision?.toFixed ? row.precision.toFixed(4) : row.precision}</td>
                      <td style={{ padding: '6px 8px', color: C.cyan, borderBottom: `1px solid ${C.b0}` }}>{row.recall?.toFixed ? row.recall.toFixed(4) : row.recall}</td>
                      <td style={{ padding: '6px 8px', color: C.violet, borderBottom: `1px solid ${C.b0}` }}>{row.f1?.toFixed ? row.f1.toFixed(4) : row.f1}</td>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{row.support}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {confusion.length > 0 && (
          <div>
            <div style={{ fontFamily: MONO, fontSize: 8, color: C.t4, letterSpacing: '.12em', marginBottom: 6 }}>CONFUSION MATRIX</div>
            <div style={{ overflow: 'auto', border: `1px solid ${C.b0}`, borderRadius: 4 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: C.deep }}>
                    <th style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>TRUE \ PRED</th>
                    {confusion[0]?.map((_, index) => (
                      <th key={index} style={{ textAlign: 'left', padding: '7px 8px', color: C.t4, borderBottom: `1px solid ${C.b1}` }}>{classLabels[index] || `Class ${index}`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {confusion.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td style={{ padding: '6px 8px', color: C.t2, borderBottom: `1px solid ${C.b0}` }}>{classLabels[rowIndex] || `Class ${rowIndex}`}</td>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ padding: '6px 8px', color: rowIndex === cellIndex ? C.green : C.t2, borderBottom: `1px solid ${C.b0}` }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReportViewer({ exp, onClose }) {
  if (!exp) return null;

  const snap    = exp.snapshot || {};
  const expType = exp.type || exp.module || 'auto_eda';
  const meta    = TYPE_META[expType] || TYPE_META.auto_eda;

  // Reconstruct ds — 50 real stored rows for Head/Tail table
  const ds = useMemo(() => {
    const headers = snap.col_headers || [];
    const rows = (snap.sample_rows || []).map(r =>
      Array.isArray(r) ? Object.fromEntries(headers.map((h, i) => [h, r[i]])) : r
    );
    return { name: snap.dataset?.name || exp.title || '', headers, rows, sampleRows: rows };
  }, [snap, exp.title]);

  // Reconstruct profile — same shape AutoEDA passes to ReportComposer
  const profile = useMemo(() => {
    const colProfilesRaw = snap.col_profiles || [];
    if (!colProfilesRaw.length) return null;

    const numCols = colProfilesRaw.filter(p => p.type === 'numeric').map(p => p.col);
    const catCols = colProfilesRaw.filter(p => p.type === 'categorical').map(p => p.col);
    const nullMap = snap.null_map || {};
    const totalNulls = Object.values(nullMap).reduce((s, n) => s + (n || 0), 0);

    const colProfiles = colProfilesRaw.map(p => {
      // hist: stored as [{x0,x1,n}], live format is [{label,count}]
      const hist = (p.hist || []).map(b => ({ label: b.x0 ?? b.label, count: b.n ?? b.count ?? 0 }));

      // valueCounts: stored as [{v,n}], live format is [{name,count,pct}]
      const rawVC   = p.valueCounts || [];
      const vcTotal = rawVC.reduce((s, vc) => s + (vc.n ?? vc.count ?? 0), 0) || 1;
      const valueCounts = rawVC.map(vc => ({
        name:  String(vc.v ?? vc.name ?? ''),
        count: vc.n ?? vc.count ?? 0,
        pct:   +(((vc.n ?? vc.count ?? 0) / vcTotal) * 100).toFixed(1),
      }));

      // histChart / valueCountsChart: exact format DashboardEngine chart components use
      // Use stored value if present (new snapshots), else convert from hist/valueCounts (old snapshots)
      const histChart = p.histChart || (hist.length ? hist : null);
      const valueCountsChart = p.valueCountsChart || (valueCounts.length ? valueCounts : null);

      return {
        col:         p.col,
        type:        p.type,
        uniqueCount: p.uniqueCount || 0,
        nullPct:     p.nullPct     || 0,
        nullCount:   0,
        stats:       p.stats || null,
        valueCounts,
        hist,
        histChart,
        valueCountsChart,
        shape: p.shape || null,
        flags: (p.flags || []).map(f => typeof f === 'string' ? { label: f, color: '#fbbf24' } : f),
      };
    });

    return {
      qualityScore: snap.quality_score ?? 0,
      quality: (snap.quality_insights || snap.insights || []).map(text => ({ icon: '●', color: C.cyan, text })),
      topCorr: (snap.correlations || []).map(c => ({ ...c, abs: Math.abs(c.r || 0) })),
      nullMap,
      nullPct:    parseFloat(snap.metrics?.null_pct)    || 0,
      dupCount:   parseInt(snap.metrics?.duplicates)    || 0,
      totalNulls,
      numCols,
      catCols,
      types: colProfiles.map(p => ({ col: p.col, type: p.type, uniqueCount: p.uniqueCount })),
      meta: {
        rows:        snap.dataset?.rows        || 0,
        cols:        snap.dataset?.cols        || 0,
        numeric:     snap.dataset?.numeric     || numCols.length,
        categorical: snap.dataset?.categorical || catCols.length,
        completeRows: 0,
      },
      colProfiles,
    };
  }, [snap]);

  // precomputed: tells ReportComposer to use stored full-dataset values
  // instead of recomputing from the 50 sample rows
  const precomputed = useMemo(() => {
    if (!profile) return null;
    return {
      col_profiles:     profile.colProfiles,
      correlations:     snap.correlations     || [],
      null_map:         snap.null_map         || {},
      quality_score:    snap.quality_score,
      quality_insights: snap.quality_insights || snap.insights || [],
      dataset:          snap.dataset          || {},
      metrics:          snap.metrics          || {},
      viz_cards:        snap.viz_cards        || [],
      corr_scatter:           snap.corr_scatter              || [],
      ml_feature_importance:  snap.ml_feature_importance    || [],
    };
  }, [snap, profile]);

  // mlViewData: supplies full-dataset values to MLReportTab so View Report matches live.
  // Uses snap_* final-form fields for new snapshots; falls back to legacy reconstruction
  // from col_profiles / null_map for old snapshots.
  const mlViewData = useMemo(() => {
    if (expType !== 'ml_experiments') return null;

    // 1. snapTypes — [{col, type, uniqueCount}]
    let snapTypes = snap.snap_types || null;
    if (!snapTypes) {
      const rawP = snap.col_profiles || [];
      if (rawP.length) snapTypes = rawP.map(p => ({ col: p.col, type: p.type, uniqueCount: p.uniqueCount || 0 }));
    }

    // 2. distColData — {typeMap, perCol} as DistributionLab expects
    let distColData = snap.snap_dist_col_data || null;
    if (!distColData) {
      const rawP = snap.col_profiles || [];
      if (rawP.length) {
        const typeMap = {}, perCol = {};
        rawP.forEach(p => {
          typeMap[p.col] = p.type;
          perCol[p.col] = {
            type:    p.type,
            stats:   p.stats ? { ...p.stats } : null,
            hist:    (p.hist || []).map(b => ({ label: b.x0 != null ? String(b.x0) : String(b.label ?? ''), count: b.n ?? b.count ?? 0 })),
            counts:  (p.valueCounts || []).map(vc => ({ name: String(vc.v ?? vc.name ?? ''), count: vc.n ?? vc.count ?? 0, pct: 0 })),
            catMeta: p.type === 'categorical' ? { unique: p.uniqueCount || 0, nulls: 0 } : null,
          };
        });
        distColData = { typeMap, perCol };
      }
    }

    // 3. corrState — reconstructed from correlations + corr_scatter (never stored separately
    //    to avoid duplicating scatter pts and hitting the 512 KB snapshot size limit)
    const corrPairs = snap.correlations || [];
    let corrState = null;
    if (corrPairs.length) {
      const scatterMap = {};
      (snap.corr_scatter || []).forEach(s => {
        scatterMap[`${s.a}__${s.b}`] = s.pts;
        scatterMap[`${s.b}__${s.a}`] = s.pts;
      });
      const colsSet = new Set();
      corrPairs.forEach(p => { colsSet.add(p.a); colsSet.add(p.b); });
      const matCols = [...colsSet].slice(0, 14);
      const pairMap = {};
      corrPairs.forEach(p => { pairMap[`${p.a}__${p.b}`] = p.r; pairMap[`${p.b}__${p.a}`] = p.r; });
      const matrix   = matCols.map(a => matCols.map(b => a === b ? 1 : (pairMap[`${a}__${b}`] ?? 0)));
      const allPairs = corrPairs.map(p => ({ ...p, pts: scatterMap[`${p.a}__${p.b}`] || null }));
      corrState = { matCols, matrix, allPairs };
    }

    // 4. nullCols — [{h, n, pct}]
    let nullCols = snap.snap_null_cols || null;
    if (!nullCols) {
      const nullMap   = snap.null_map || {};
      const totalRows = snap.dataset?.rows || 0;
      if (totalRows > 0) {
        nullCols = Object.entries(nullMap)
          .filter(([, n]) => n > 0)
          .map(([col, n]) => ({ h: col, n, pct: +((n / totalRows) * 100).toFixed(1) }));
      }
    }

    return {
      distColData,
      corrState,
      snapTypes,
      featImportance: snap.ml_feature_importance || [],
      totalRows:      snap.dataset?.rows || 0,
      nullCols,
    };
  }, [snap, expType]);

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 10100,
        background: 'rgba(2,5,8,.94)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: C.surface, border: `1px solid ${C.b2}`, borderRadius: 8,
        width: '100%', maxWidth: 940, boxShadow: '0 32px 100px rgba(0,0,0,.9)',
        marginBottom: 40,
      }}>
        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 24px 18px', background: C.deep,
          borderBottom: `1px solid ${C.b1}`, borderRadius: '8px 8px 0 0', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: MONO, fontSize: 8, color: meta.color,
                background: `${meta.color}14`, border: `1px solid ${meta.color}40`,
                borderRadius: 2, padding: '2px 8px', fontWeight: 700, letterSpacing: '.1em',
              }}>{meta.icon} {meta.label}</span>
              {exp.author && <span style={{ fontFamily: MONO, fontSize: 9, color: C.t3 }}>by {exp.author}</span>}
              {exp.time   && <span style={{ fontFamily: MONO, fontSize: 8, color: C.t4 }}>{exp.time}</span>}
            </div>
            <h1 style={{ fontFamily: SYNE, fontWeight: 800, fontSize: 22, color: C.t1, margin: '0 0 6px', lineHeight: 1.2 }}>
              {exp.title || 'Experiment Report'}
            </h1>
            {exp.abstract && (
              <p style={{ fontFamily: MONO, fontSize: 10, color: C.t3, margin: 0, lineHeight: 1.6 }}>
                {exp.abstract}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: `1px solid ${C.b1}`, color: C.t3,
              borderRadius: 3, cursor: 'pointer', fontSize: 18, lineHeight: 1,
              padding: '4px 10px', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.color = C.red; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.b1; e.currentTarget.style.color = C.t3; }}
          >×</button>
        </div>

        {/* Body — ML Report or AutoEDA Report depending on expType */}
        {expType === 'ml_experiments' ? (
          <MLReportTab
            experiment={{
              name:      exp.title || '',
              readOnly: true,
              ds,
              mlConfig: {
                targetCol:     snap.ml_config?.targetCol     || '',
                featureCols:   snap.ml_config?.featureCols   || [],
                encodings:     snap.ml_config?.encodings     || {},
                scalingMethod: snap.ml_config?.scalingMethod || {},
                pcaResult:     snap.ml_config?.pcaResult     || null,
              },
              mlResults: Object.fromEntries(
                (snap.ml_results || []).map(r => [r.algoId, r])
              ),
              notes: snap.notes || [],
            }}
            dashboardCards={snap.dashboard_cards || []}
            viewData={mlViewData}
            reportState={snap.report_state || (snap.notes?.length ? { notes: snap.notes } : null)}
            readOnly
          />
        ) : expType === 'dl_experiments' ? (
          <DLPublicReport exp={exp} snap={snap} ds={ds} />
        ) : (
        <ReportComposer
          experiment={{ ds }}
          dashboardCards={snap.dashboard_cards || []}
          profile={profile}
          precomputed={precomputed}
          reportState={snap.report_state || null}
          readOnly
        />
        )}
      </div>
    </div>
  );
}
